import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';

import { SecondaryButton } from '@rebound/design-system/components/Button';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import { Order } from '@rebound/shared-models/order/order.model';
import {
  Retailer,
  ThemeColorScheme,
  UserJourneyEnum,
} from '@rebound/shared-models/retailer/retailer.model';
import { CycleonReturnOrderStatusEnum } from '@rebound/shared-models/return/return.model';

import OrderCard, {
  OrderCardType,
} from '@modules/dashboard/components/OrderCard';
import ReturnNotAvailableLabel from '@modules/dashboard/components/ReturnNotAvailableLabel';
import ViewMultipleReturnsButton, {
  ViewMultipleReturnsButtonType,
} from '@modules/dashboard/components/ViewMultipleReturnsButton';

type OrderDetailsProps = {
  order: Order;
  closeModal: () => void;
  retailer: Retailer | undefined;
  returnAvailable: boolean | undefined;
};

const OrderLookupDetails = ({
  order,
  closeModal,
  retailer,
  returnAvailable,
}: OrderDetailsProps) => {
  const { t } = useTranslation();

  const orderHasOneReturn =
    order?.returns?.length === 1 &&
    order?.returns[0]?.status === CycleonReturnOrderStatusEnum.DONE;

  const orderHasMultipleReturns =
    order?.returns !== undefined && order?.returns?.length > 1;

  return (
    <>
      <Title>{t('orderLookup.titleTwo') as string}</Title>
      {retailer?.userJourneyRadio === UserJourneyEnum.EMAIL_VERIFICATION && (
        <Description>{t('orderLookup.descriptionTwo') as string}</Description>
      )}
      <OrderWrapper data-cy="lookup-matched-order">
        <OrderCard
          order={order}
          type={OrderCardType.LookupPreview}
          scheme={retailer?.theme.colorScheme}
          returnPolicyDays={retailer?.returnPolicyDays}
        />
      </OrderWrapper>
      {!order.returns.length && returnAvailable ? (
        <ControlsWrapper>
          <StyledSecondaryButton
            scheme={retailer?.theme.colorScheme || ThemeColorScheme.Indigo}
            onClick={() => closeModal()}
          >
            {retailer?.userJourneyRadio === UserJourneyEnum.EMAIL_VERIFICATION
              ? (t('orderLookup.backToReturnsCentreButton') as string)
              : (t('orderLookup.backToHomePage') as string)}
          </StyledSecondaryButton>
        </ControlsWrapper>
      ) : !order.returns.length && !returnAvailable ? (
        retailer?.userJourneyRadio === UserJourneyEnum.ORDER_LOOKUP ? (
          <ControlsWrapper>
            <StyledSecondaryButton
              scheme={retailer?.theme.colorScheme || ThemeColorScheme.Indigo}
              onClick={() => closeModal()}
            >
              {t('orderLookup.backToHomePage') as string}
            </StyledSecondaryButton>
          </ControlsWrapper>
        ) : (
          <ReturnNotAvailableLabelWrapper>
            <ReturnNotAvailableLabel
              order={order}
              returnPolicyDays={retailer?.returnPolicyDays ?? 30}
            />
          </ReturnNotAvailableLabelWrapper>
        )
      ) : order.returns.length ? (
        <ControlsWrapper>
          <StyledSecondaryButton
            scheme={retailer?.theme.colorScheme || ThemeColorScheme.Indigo}
            onClick={() => closeModal()}
          >
            {retailer?.userJourneyRadio === UserJourneyEnum.EMAIL_VERIFICATION
              ? (t('ordersListing.cancelReturnLabel') as string)
              : (t('orderLookup.backToHomePage') as string)}
          </StyledSecondaryButton>

          {orderHasOneReturn ? (
            <Link
              href={
                retailer?.userJourneyRadio ===
                UserJourneyEnum.EMAIL_VERIFICATION
                  ? `/dashboard/returns?highlightReturn=${order.returns[0].id}`
                  : `/track-return?orderReference=${order.returns[0].orderReference}`
              }
              passHref
            >
              <PrimaryButton
                data-cy="view-return-button"
                scheme={retailer?.theme.colorScheme || ThemeColorScheme.Indigo}
              >
                {t('ordersListing.viewReturnLabel') as string}
              </PrimaryButton>
            </Link>
          ) : orderHasMultipleReturns ? (
            retailer?.userJourneyRadio ===
            UserJourneyEnum.EMAIL_VERIFICATION ? (
              <ViewMultipleReturnsButton
                type={ViewMultipleReturnsButtonType.LookupPreview}
                userJourney={retailer?.userJourneyRadio as UserJourneyEnum}
                orderReference={order.orderReference}
                returnIds={order.returns
                  .filter((r) => r.status === CycleonReturnOrderStatusEnum.DONE)
                  .map((r) =>
                    retailer?.userJourneyRadio ===
                    UserJourneyEnum.EMAIL_VERIFICATION
                      ? r.id
                      : r.orderReference,
                  )}
              />
            ) : (
              <Link
                href={`/track-return?orderReference=${order.returns[0].orderReference}`}
                passHref
              >
                <PrimaryButton
                  scheme={
                    retailer?.theme.colorScheme || ThemeColorScheme.Indigo
                  }
                >
                  {t('ordersListing.viewReturnLabel') as string}
                </PrimaryButton>
              </Link>
            )
          ) : null}
        </ControlsWrapper>
      ) : null}
    </>
  );
};

const Title = styled.p`
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: ${(props) => props.theme.colors.grayDark4};
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.grayDark};
  text-align: center;
  max-width: 280px;
  margin-bottom: 20px;

  ${up('sm')} {
    max-width: 470px;
    margin-bottom: 24px;
  }
`;

const OrderWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  ${up('lg')} {
    padding: 0 90px;
  }

  img {
    z-index: 0 !important;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -12px;

  ${down('sm')} {
    width: 100%;
    margin-top: 0;
    flex-direction: column;
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  padding: 12px 0;
  width: 100%;

  ${up('sm')} {
    width: 240px;
    margin-right: 24px;
  }

  ${down('sm')} {
    margin-bottom: 12px;
  }
`;

const PrimaryButton = styled.button<{ scheme: string }>`
  padding: 12px 0;
  text-align: center;
  background-color: ${(props) => colorSchemeConfig[props.scheme].v700};
  color: ${(props) => props.theme.colors.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  width: 100%;

  ${up('sm')} {
    width: 240px;
    margin-right: 24px;
  }

  ${down('xs')} {
    margin-bottom: 12px;
  }
`;

const ReturnNotAvailableLabelWrapper = styled.div`
  width: 100%;
  margin-top: -12px;

  ${up('sm')} {
    display: flex;
    justify-content: center;
  }
`;

export default OrderLookupDetails;
